import { TariffCard } from '@/components/global/TariffCard';
import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Box, Dialog, Grid, IconButton } from '@mui/material';
import { CloseIcon } from '@/icons';
import { initializePaddle } from '@paddle/paddle-js';
import {
  confirmPaddlePaymentIntent,
  getPaddleClientSideToken,
  getPaddlePriceId,
  getSubscriptionPlans,
  SubscriptionPlanDto,
  SubscriptionType,
} from '@tailored-pdf/api';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Header, SubHeader } from './styles';
import { fireSubscribedConversion, logEvent } from '@/helpers/eventLogger';
import { ROUTES } from '@/constants/constants';
import { useNavigate } from 'react-router-dom';

const isDev = location.hostname !== 'tailored-pdf.com';

let checkoutSubscriptionType: SubscriptionType | undefined = undefined;

function PaymentModal() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const {
    state: { subscriptionPlans, userInfo, preselectedTariff, uploadedFileId },
    dispatch,
  } = useContext(Context);

  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useNotifications();

  const [paddleId, setPaddleId] = useState<string>();
  const [paddleInited, setPaddleInited] = useState<boolean>(false);

  // Paddle's eventCallback is registered once (guarded by !paddleInited), so it would
  // capture userInfo from first render. Keep the latest email in a ref so the Enhanced
  // Conversions payload always reflects the signed-in user at checkout time.
  const emailRef = useRef<string | undefined>(userInfo?.email);
  emailRef.current = userInfo?.email;

  const loadPaddleConfig = useCallback(async () => {
    try {
      const resp = await getPaddleClientSideToken();
      setPaddleId(resp.data);
    } catch (e) {
      notifyError(getResponseError(e));
    }
  }, [notifyError]);

  const loadPlans = useCallback(async () => {
    try {
      const resp = await getSubscriptionPlans();
      dispatch({
        type: AppStateActions.SET_SUBSCRIPTION_PLANS,
        payload: { subscriptionPlans: resp.data },
      });
    } catch (e) {
      notifyError(getResponseError(e));
    }
  }, [dispatch, notifyError]);

  const changeSubscription = useCallback(
    async (subscriptionType: SubscriptionType) => {
      try {
        const resp = await getPaddlePriceId({ subscriptionType: subscriptionType });
        isDev && window.Paddle?.Environment.set('sandbox');
        checkoutSubscriptionType = subscriptionType;
        userInfo &&
          window.Paddle?.Checkout.open({
            items: [{ priceId: resp.data, quantity: 1 }],
            customer: { email: userInfo.email },
            settings: {
              displayMode: 'overlay',
            },
          });
        logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_LINK_FOLLOWED' });
      } catch (e) {
        notifyError(getResponseError(e));
      }
    },
    [notifyError, userInfo],
  );

  const handleClose = useCallback(() => {
    dispatch({ type: AppStateActions.SET_NEED_PAYMENT, payload: { needPayment: false, preselectedTariff: undefined } });
    checkoutSubscriptionType = undefined;
  }, [dispatch]);

  const handleChangeSubscription = useCallback(
    (plan: SubscriptionPlanDto) => {
      void changeSubscription(plan.subscriptionType);
    },
    [changeSubscription],
  );

  const handleConfirmPayment = useCallback(
    async (customerId: string) => {
      try {
        if (checkoutSubscriptionType) {
          await confirmPaddlePaymentIntent({ customerId, subscriptionType: checkoutSubscriptionType });
          dispatch({
            type: AppStateActions.SET_LAST_PAYMENT_DATE,
            payload: { lastPaymentDate: new Date().toLocaleString() },
          });
          notifySuccess(t('success'));
          window.Paddle?.Checkout.close();
          handleClose();
          uploadedFileId && navigate(ROUTES.results(i18n.language, uploadedFileId));
        }
      } catch (e) {
        notifyError(getResponseError(e));
      }
    },
    [dispatch, handleClose, i18n.language, navigate, notifyError, notifySuccess, t, uploadedFileId],
  );

  useEffect(() => {
    void loadPaddleConfig();
    void loadPlans();
  }, [loadPaddleConfig, loadPlans]);

  useEffect(() => {
    preselectedTariff && paddleInited && changeSubscription(preselectedTariff);
  }, [changeSubscription, paddleInited, preselectedTariff]);

  useEffect(() => {
    paddleId &&
      !paddleInited &&
      initializePaddle({
        token: paddleId,
        // handle events
        eventCallback: function (data) {
          isDev && console.log(data);
          if (data.name === 'checkout.completed') {
            fireSubscribedConversion(emailRef.current);
            logEvent({ logType: 'EVENT', eventType: 'CHECKOUT_SUCCEEDED' });
            data.data?.customer.id && handleConfirmPayment(data.data.customer.id);
          } else if (data.name === 'checkout.payment.failed') {
            logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: data.data });
          } else if (data.name === 'checkout.closed') {
            preselectedTariff && handleClose();
          }
        },
        debug: isDev,
      }).then(() => {
        setPaddleInited(true);
      });
  }, [handleClose, handleConfirmPayment, paddleId, paddleInited, preselectedTariff]);

  const trialPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.TRIAL);
  const monthlyPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.MONTHLY);
  const yearlyPlan = subscriptionPlans?.find((v) => v.subscriptionType === SubscriptionType.YEARLY);

  return preselectedTariff ? null : (
    <Dialog
      open
      maxWidth={'lg'}
      fullWidth
      disableEscapeKeyDown
      onClose={(_event, reason) => {
        // Only the close button may dismiss the modal — ignore backdrop clicks
        // (escape is disabled) to avoid accidental drop-off and hurting conversion.
        if (reason === 'backdropClick') return;
        handleClose();
      }}
    >
      <Box display={'flex'} flexDirection={'column'} padding={8} flex={'1 1 auto'}>
        <IconButton
          onClick={handleClose}
          aria-label="close"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <Header>
          <Trans
            t={t}
            i18nKey="title"
            components={{
              accent: <Box color={'primary.main'} display={'inline-flex'} />,
            }}
          />
        </Header>
        <SubHeader>{t('subtitle')}</SubHeader>

        <Grid container spacing={8}>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={trialPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={monthlyPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
          <Grid item xs={12} md={4} display={'flex'} justifyContent={'center'}>
            <TariffCard
              plan={yearlyPlan}
              plans={subscriptionPlans}
              onAction={handleChangeSubscription}
              nextSubscriptionType={userInfo?.nextSubscriptionType}
              nextSubscriptionStartDate={userInfo?.nextSubscriptionStartDate}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}

export { PaymentModal };
